import { css, html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';

import * as Tags from './user-menus.tagnames';
import { findCssRules, getGlobalStyleSheets } from '../../global-styles';
import { IconEnums } from '../icon';
import { AtlUserNotificationsMenuArticleAvatarProps } from './user-menus.props';
import { ButtonEnums } from '../button';
import { AtlUserNotificationsArticleActionEvent } from './user-menus.events';
import { ifDefined } from 'lit/directives/if-defined.js';

const DESCRIPTION_SLOT = 'Description';
const TITLEINDICATOR_SLOT = 'TitleIndicator';

@customElement(Tags.UserNotificationsPanelSectionArticle)
export class AtlUserNotificationsPanelSectionArticle extends LitElement {
   declare avatar?: AtlUserNotificationsMenuArticleAvatarProps;
   declare articleTitle?: string;
   declare timeSpan?: string;
   declare ctaButtonLabel?: string;

   static styles = [
      ...getGlobalStyleSheets(),
      unsafeCSS(findCssRules('.panel-section-article')),
      css`
         :host {
            display: block;
            width: 100%;
         }
         .panel-section-article {
            display: flex;
            width: 100%;
         }
      `,
   ];

   static properties = {
      avatar: { type: Object },
      articleTitle: { attribute: 'article-title', type: String },
      timeSpan: { type: String },
      ctaButtonLabel: { type: String },
   };

   private _addParentDivClassToSlot(
      parentElement: string,
      slotName: string,
      parentClassName: string
   ) {
      const slot = this.shadowRoot?.querySelector(`slot[name="${slotName}"]`);

      const assignedNodes = (slot as HTMLSlotElement)?.assignedNodes({
         flatten: false,
      });

      const parent = document.createElement(parentElement);
      parent.classList.add(parentClassName);

      assignedNodes?.forEach((node) => {
         if (node.nodeType === Node.ELEMENT_NODE) {
            parent.appendChild(node);
         }
      });
      slot?.parentNode!.replaceChild(parent, slot);
   }

   private _renderCtaButton = () => {
      if (!this.ctaButtonLabel) return nothing;

      return html`
         <div class="article-cta">
            <atlantis-button
               variant=${ButtonEnums.ButtonVariant.Action}
               @click=${() =>
                  this.dispatchEvent(
                     new AtlUserNotificationsArticleActionEvent()
                  )}
               >${this.ctaButtonLabel}</atlantis-button
            >
         </div>
      `;
   };

   private _renderTitle = () => {
      const slotContent = html`<slot
         name="${TITLEINDICATOR_SLOT}"
         @slotchange=${() =>
            this._addParentDivClassToSlot(
               'div',
               TITLEINDICATOR_SLOT,
               'title-indicator'
            )}
      ></slot>`;

      if (!this.articleTitle) return slotContent;

      return html`
         <div class="article-body-title">
            <div class="title-root">
               <strong>${this.articleTitle}</strong>
               <small class="text-secondary">${this.timeSpan}</small>
            </div>
            ${slotContent}
         </div>
      `;
   };

   private _renderAvatar = () => {
      if (!this.avatar) return nothing;

      const avatarContent = this.avatar.avatarLabel
         ? html`${this.avatar.avatarLabel.substring(0, 2)}`
         : html`<atlantis-icon
              iconSource=${IconEnums.IconSource.FontAwesome}
              name=${ifDefined(this.avatar.avatarIconName)}
           ></atlantis-icon>`;

      return html`
         <div class="article-avatar">
            <div class="avatar-root">${avatarContent}</div>
         </div>
      `;
   };

   render() {
      return html`
         <article class="panel-section-article">
            ${this._renderAvatar()}
            <div class="article-body">
               ${this._renderTitle()}
               <slot
                  name="${DESCRIPTION_SLOT}"
                  @slotchange=${() =>
                     this._addParentDivClassToSlot(
                        'p',
                        DESCRIPTION_SLOT,
                        'article-body-description'
                     )}
               ></slot>
            </div>
            ${this._renderCtaButton()}
         </article>
      `;
   }
}
